import { Plus, Search } from "lucide-react";
import { useState, useRef } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { BrandAd } from "@/screens/analysis/types";
import { createFolder } from "@/screens/analysis/apiService";
import { useParams } from "react-router-dom";
import { useCommentAnalysisStore } from "@/screens/analysis/store";
import { Button } from "@/components/ui/button";
import { BaseActionProps } from "@/components/common/adCard/types";
import { RiBookmarkFill, RiBookmarkLine } from "@remixicon/react";

interface BookmarkAdProps extends BaseActionProps {
  ad: BrandAd;
  onBookmark?: (folderId: string) => void;
  folderId?: string;
}

const BookmarkAd = ({ ad, onBookmark, folderId }: BookmarkAdProps) => {
  const { accountId } = useParams();
  const { folders, setFolders } = useCommentAnalysisStore();
  const [folderSearchQuery, setFolderSearchQuery] = useState<string>("");
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false);
  const createFolderInputRef = useRef<HTMLInputElement>(null);
  const folderListRef = useRef<HTMLDivElement>(null);
  const folderSearchInputRef = useRef<HTMLInputElement>(null);

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearchQuery.toLowerCase()),
  );

  const handleCreateFolder = async () => {
    if (!accountId) return;
    try {
      const folderName = createFolderInputRef.current?.value.trim();
      const nameExists = folders.some((folder) => folder.name === folderName);
      if (nameExists) {
        console.error("A folder with this name already exists");
        return;
      }
      if (folderName) {
        const res = await createFolder({ accountId, name: folderName });
        setFolders([...folders, res]);
        if (folderListRef.current) {
          requestAnimationFrame(() => {
            folderListRef.current?.scrollTo({
              top: folderListRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        }
      }
      setIsCreatingFolder(false);
    } catch (error) {
      console.error("Error creating folder:", error);
      setIsCreatingFolder(false);
    }
  };

  const BookmarkButton = ({ onClick }: { onClick?: () => void }) => (
    <Button
      className="pointer-events-auto squircle-mask squircle-smooth-[1] squircle-radius-[8px] bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] border-0 text-[12px] font-normal leading-[16px] text-[#fff] h-[30px] w-[30px] cursor-pointer focus:outline-none focus:ring-0"
      onClick={onClick}
    >
      {ad.folders.length > 0 ? (
        <RiBookmarkFill size={14} className="text-white shrink-0" />
      ) : (
        <RiBookmarkLine size={14} className="text-white shrink-0" />
      )}
    </Button>
  );

  return (
    <div className="pointer-events-auto">
      {folderId ? (
        <BookmarkButton
          onClick={() => {
            if (folderId) {
              onBookmark?.(folderId);
            }
          }}
        />
      ) : (
        <DropdownMenu
          onOpenChange={(open) => {
            if (!open) setIsCreatingFolder(false);
          }}
        >
          <DropdownMenuTrigger
            asChild
            onPointerDown={(e) => {
              if (ad.folders.length > 0) {
                e.preventDefault();
                onBookmark?.(ad.folders[0]?.id);
              }
            }}
          >
            <div className="pointer-events-auto">
              <BookmarkButton />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="bottom"
            className="rounded-[12px] border-[0.8px] border-border-gray shadow-sm p-0 w-[185px]"
          >
            <div className="flex items-center gap-2 px-2 py-2 border-b border-border-gray">
              <Search size={16} strokeWidth={1.5} color="#021859" className="shrink-0" />
              <input
                ref={folderSearchInputRef}
                type="text"
                placeholder="Search"
                value={folderSearchQuery}
                onChange={(e) => {
                  setFolderSearchQuery(e.target.value);
                  e.stopPropagation();
                }}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="w-full bg-transparent outline-none placeholder:text-brand-primary-400 text-brand-primary-new font-['Helvetica_Neue'] text-[13px] font-normal leading-[20px]"
              />
            </div>

            <div ref={folderListRef} className="p-1 max-h-[240px] overflow-y-auto">
              {filteredFolders.map((folder) => (
                <DropdownMenuItem
                  key={folder.id}
                  className="focus:bg-transparent hover:bg-[#F6F6F6] focus:text-inherit cursor-pointer flex items-center gap-2"
                  onSelect={() => onBookmark?.(folder.id)}
                >
                  {folder.name}
                </DropdownMenuItem>
              ))}
            </div>

            <Separator className="w-full text-[#E4E4E7]" />

            <div className="px-1 py-0 h-9">
              {!isCreatingFolder ? (
                <button
                  className="flex items-center gap-2 w-full cursor-pointer rounded-[4px] h-8 px-2 py-[6px]"
                  onClick={() => setIsCreatingFolder(true)}
                >
                  <Plus size={16} color="#FF4500" />
                  <span className="font-['Helvetica_Neue'] text-[13px] font-normal leading-[20px] text-[#FF4500]">
                    Create folder
                  </span>
                </button>
              ) : (
                <Input
                  ref={createFolderInputRef}
                  placeholder="Folder name"
                  className="w-full h-8 p-2 border-0 rounded-[4px] text-[14px] focus:outline-none shadow-none leading-none"
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      void handleCreateFolder();
                    } else if (e.key === "Escape") {
                      setIsCreatingFolder(false);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={() => setIsCreatingFolder(false)}
                />
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default BookmarkAd;
